import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import { formatDrillAmount, formatDrillDate } from "./helpers/formatters";
import BookkeepingService from "../../../../../services/BookkeepingService";
import WComponent from "../../../../common/WComponent";
import "../afs-walkthrough.css";

const NUMERIC_SORT_COLUMNS = ["tax", "debit", "credit"];

const SORT_ACCESSORS = {
	date: row => (row.date ? new Date(row.date).getTime() : 0),
	description: row => (row.description || row.merchant || "").toLowerCase(),
	tax: row => Number(row.taxAmount) || 0,
	debit: row => Number(row.debit) || 0,
	credit: row => Number(row.credit) || 0,
};

function applySort({ rows, sort }) {
	const sortValue = SORT_ACCESSORS[sort.column] || SORT_ACCESSORS.description;
	const direction = sort.dir === "asc" ? 1 : -1;
	return [...rows].sort((a, b) => {
		const valueA = sortValue(a);
		const valueB = sortValue(b);
		if (valueA < valueB) return -1 * direction;
		if (valueA > valueB) return 1 * direction;
		return 0;
	});
}

class DrilldownPanel extends WComponent {
	state = {
		search: "",
		csvRows: [],
		loading: false,
		loadError: null,
		csvSort: { column: "date", dir: "asc" },
		glSort: { column: "date", dir: "asc" },
		panelWidth: 420,
	};

	_resizeStartX = 0;
	_resizeStartWidth = 0;

	// Guards so a slow/stale fetch can't overwrite state after the account
	// changes or the panel unmounts (replaces the old effect's `cancelled` flag).
	_cancelled = false;
	_reqToken = 0;

	componentDidMount() {
		this.fetchRows();
	}

	componentDidUpdate(prevProps) {
		let prevAcct = (prevProps.account.accountNumber || "").trim();
		let acct = (this.props.account.accountNumber || "").trim();
		if (prevProps.clientId !== this.props.clientId || prevAcct !== acct) {
			this.fetchRows();
		}
	}

	componentWillUnmount() {
		this._cancelled = true;
		this.stopResize();
	}

	onResizeStart = event => {
		event.preventDefault();
		this._resizeStartX = event.clientX;
		this._resizeStartWidth = this.state.panelWidth;
		document.addEventListener("mousemove", this.onResizeMove);
		document.addEventListener("mouseup", this.stopResize);
	};

	onResizeMove = event => {
		const widthChange = this._resizeStartX - event.clientX;
		const requestedWidth = this._resizeStartWidth + widthChange;
		const clampedWidth = Math.max(340, Math.min(requestedWidth, 900));
		this.setState({ panelWidth: clampedWidth });
	};

	stopResize = () => {
		document.removeEventListener("mousemove", this.onResizeMove);
		document.removeEventListener("mouseup", this.stopResize);
	};

	fetchRows = async () => {
		let clientId = this.props.clientId;
		let acctNum = (this.props.account.accountNumber || "").trim();
		if (!clientId || !acctNum) {
			this.update({ csvRows: [] });
			return;
		}
		let token = ++this._reqToken;
		this.update({ loading: true, loadError: null });
		try {
			let result = await BookkeepingService.getTransactionsForAccount({ clientId, accountNumber: acctNum });
			if (this._cancelled || token !== this._reqToken) return;
			if (result && Array.isArray(result.transactions)) {
				this.update({ csvRows: result.transactions });
			} else {
				this.update({ csvRows: [], loadError: (result && result.message) || "Failed to load transactions" });
			}
		} catch (error) {
			if (this._cancelled || token !== this._reqToken) return;
			this.update({ csvRows: [], loadError: "Failed to load transactions" });
		} finally {
			if (!this._cancelled && token === this._reqToken) this.update({ loading: false });
		}
	};

	render() {
		let { account, glEntries, blMonthly, annotations, onClose } = this.props;
		let { search, csvRows, loading, loadError, csvSort, glSort, panelWidth } = this.state;

		const acctNum = (account.accountNumber || "").trim();

		const glRows = glEntries ? (glEntries[acctNum] || []) : null;
		const q = search.trim().toLowerCase();

		const csvDrilldown = BookkeepingService.buildAccountDrilldownRows({ transactions: csvRows, invertSign: true, taxIsAmount: false });
		const glDrilldown = glRows ? BookkeepingService.buildAccountDrilldownRows({ transactions: glRows, invertSign: false, taxIsAmount: true }) : null;

		const filteredCsv = q ? csvDrilldown.rows.filter(tx => (tx.description || tx.merchant || "").toLowerCase().includes(q)) : csvDrilldown.rows;
		const filteredGl  = glDrilldown && q ? glDrilldown.rows.filter(tx => (tx.description || "").toLowerCase().includes(q)) : (glDrilldown ? glDrilldown.rows : null);

		const sortedCsv = applySort({ rows: filteredCsv, sort: csvSort });
		const sortedGl  = filteredGl ? applySort({ rows: filteredGl, sort: glSort }) : null;

		const csvTotal = csvRows.reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
		const glTotal  = glRows ? glRows.reduce((s, tx) => s + (Number(tx.amount) || 0), 0) : null;

		function toggleSort({ sort, column, setSort }) {
			if (sort.column === column) {
				setSort({ column, dir: sort.dir === "asc" ? "desc" : "asc" });
				return;
			}
			const defaultDir = NUMERIC_SORT_COLUMNS.includes(column) ? "desc" : "asc";
			setSort({ column, dir: defaultDir });
		}

		const acctAnnotationData = annotations ? (annotations[acctNum] || null) : null;
		const acctAnnotationCount = acctAnnotationData ? acctAnnotationData.count : 0;
		const acctAnnotationItems = acctAnnotationData ? (acctAnnotationData.items || []) : [];
		const monthlyData = blMonthly ? (blMonthly[acctNum] || null) : null;
		const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

		const TxList = ({ rows, emptyMsg, sort, onSort }) => {
			const arrow = col => sort.column !== col ? null : (
				<Icon.ChevronDown
					size={11}
					className={clsx("afs-drill__tx-head-arrow", sort.dir === "asc" && "afs-drill__tx-head-arrow--asc")}
				/>
			);
			const headClass = col => clsx("afs-drill__tx-head-cell", sort.column === col && "afs-drill__tx-head-cell--active");

			if (rows.length === 0) return <div className="afs-drill__section-empty">{emptyMsg}</div>;

			const totalTax = rows.reduce((sum, tx) => sum + (tx.taxAmount || 0), 0);
			const totalDebit = rows.reduce((sum, tx) => sum + (tx.debit || 0), 0);
			const totalCredit = rows.reduce((sum, tx) => sum + (tx.credit || 0), 0);

			return (
				<>
					<div className="afs-drill__tx afs-drill__tx--head">
						<span className={headClass("date")} onClick={() => onSort("date")}>Date{arrow("date")}</span>
						<span className={headClass("description")} onClick={() => onSort("description")}>Description{arrow("description")}</span>
						<span className={clsx(headClass("tax"), "afs-drill__tx-num")} onClick={() => onSort("tax")}>Tax{arrow("tax")}</span>
						<span className={clsx(headClass("debit"), "afs-drill__tx-num")} onClick={() => onSort("debit")}>Debit{arrow("debit")}</span>
						<span className={clsx(headClass("credit"), "afs-drill__tx-num")} onClick={() => onSort("credit")}>Credit{arrow("credit")}</span>
					</div>
					{rows.map((tx, i) => (
						<div key={i} className="afs-drill__tx">
							<span className="afs-drill__tx-date">{formatDrillDate(tx.date)}</span>
							<span className="afs-drill__tx-desc" title={tx.description || tx.merchant}>
								{tx.description || tx.merchant || "—"}
								{tx.isTaxLeg && <span className="afs-drill__tx-leg">GST/HST on {tx.classification}</span>}
							</span>
							<span className="afs-drill__tx-num afs-drill__tx-tax" title={tx.taxLeftInPlace ? "Tax the label names that stayed in this account" : undefined}>
								{tx.taxAmount ? formatDrillAmount(tx.taxAmount) : tx.taxLeftInPlace ? `(${formatDrillAmount(tx.taxLeftInPlace)})` : "—"}
							</span>
							<span className="afs-drill__tx-num">{tx.debit ? formatDrillAmount(tx.debit) : "—"}</span>
							<span className="afs-drill__tx-num">{tx.credit ? formatDrillAmount(tx.credit) : "—"}</span>
						</div>
					))}
					<div className="afs-drill__tx afs-drill__tx--total">
						<span className="afs-drill__tx-date" />
						<span className="afs-drill__tx-desc">Total</span>
						<span className="afs-drill__tx-num afs-drill__tx-tax">{totalTax ? formatDrillAmount(totalTax) : "—"}</span>
						<span className="afs-drill__tx-num">{formatDrillAmount(totalDebit)}</span>
						<span className="afs-drill__tx-num">{formatDrillAmount(totalCredit)}</span>
					</div>
				</>
			);
		};

		let currentYearEmptyMsg;
		if (loading) currentYearEmptyMsg = "Loading…";
		else if (loadError) currentYearEmptyMsg = loadError;
		else currentYearEmptyMsg = "No bank transactions classified to this account";

		return (
			<div className="afs-drill" style={{ width: panelWidth }}>
				<div className="afs-drill__resize" onMouseDown={this.onResizeStart} title="Drag to resize" />
				<div className="afs-drill__header">
					<div className="afs-drill__header-top">
						<div>
							{account.accountNumber && <div className="afs-drill__acct-num">{account.accountNumber}</div>}
							<h3 className="afs-drill__acct-name">{account.description}</h3>
							{acctAnnotationCount > 0 && (
								<div className="afs-drill__annotations">
									{acctAnnotationItems.map(function (item, itemIndex) {
										return (
											<span key={itemIndex} className="afs-drill__annotation-badge">
												{item.label}
											</span>
										);
									})}
								</div>
							)}
						</div>
						<button className="afs-drill__close" onClick={onClose}><Icon.X size={18} /></button>
					</div>
					<div className="afs-drill__totals">
						<div className="afs-drill__total-box">
							<span className="afs-drill__total-label">Current Year</span>
							<span className="afs-drill__total-value">{formatDrillAmount(csvTotal)}</span>
						</div>
						{glRows !== null && (
							<div className="afs-drill__total-box">
								<span className="afs-drill__total-label">Prior Year</span>
								<span className="afs-drill__total-value">{formatDrillAmount(glTotal)}</span>
							</div>
						)}
						<div className="afs-drill__total-box">
							<span className="afs-drill__total-label">AFS Prior</span>
							<span className="afs-drill__total-value">{formatDrillAmount(account.prior)}</span>
						</div>
					</div>
				</div>
				<div className="afs-drill__search-bar">
					<input className="afs-drill__search-input" placeholder="Search entries…" value={search} onChange={e => this.update({ search: e.target.value })} />
				</div>
				<div className="afs-drill__body">
					<div className="afs-drill__section-header">
						<span>Current Year</span>
						<span className="afs-drill__section-count">{sortedCsv.length} entries · {formatDrillAmount(csvTotal)}</span>
					</div>
					<TxList
						rows={sortedCsv}
						emptyMsg={currentYearEmptyMsg}
						sort={csvSort}
						onSort={column => toggleSort({ sort: csvSort, column, setSort: v => this.update({ csvSort: v }) })}
					/>

					{glRows !== null && (
						<>
							<div className="afs-drill__section-header afs-drill__section-header--spaced">
								<span>Prior Year (GL)</span>
								<span className="afs-drill__section-count">{(sortedGl || []).length} entries · {formatDrillAmount(glTotal)}</span>
							</div>
							<TxList
								rows={sortedGl || []}
								emptyMsg="No adjusting entries — prior year balance from CaseWare closing balances"
								sort={glSort}
								onSort={column => toggleSort({ sort: glSort, column, setSort: v => this.update({ glSort: v }) })}
							/>
						</>
					)}

					{monthlyData && (
						<>
							<div className="afs-drill__section-header afs-drill__section-header--spaced">
								<span>Monthly Activity (Prior Year)</span>
								<span className="afs-drill__section-count">{formatDrillAmount(monthlyData.reduce((s, v) => s + v, 0))} total</span>
							</div>
							<div className="afs-drill__monthly-grid">
								{monthlyData.map((amt, i) => (
									<div key={i} className="afs-drill__monthly-cell">
										<div className="afs-drill__monthly-label">{MONTH_LABELS[i]}</div>
										<div className={"afs-drill__monthly-amt afs-drill__monthly-amt--" + (amt < 0 ? "neg" : amt > 0 ? "pos" : "zero")}>
											{amt !== 0 ? formatDrillAmount(amt) : "—"}
										</div>
									</div>
								))}
							</div>
						</>
					)}
				</div>
			</div>
		);
	}
}

export default DrilldownPanel;
